/**
 * The refusal vocabulary of `packages/money`.
 *
 * A package cannot import `apps/api`, so it cannot throw an `ApiError` — and it should not:
 * "this rate is missing" is a fact about money, not about HTTP. It throws a `MoneyError`
 * with a **stable code**, and the API maps the code to its own status exactly once
 * (`platform/errors.ts`). That mapping is what stops a refusal reaching `onError` as an
 * unrecognized throw and becoming a 500 — which for invariant 04 would be the difference
 * between "we refused to guess your exchange rate" and "the server broke".
 */
export type MoneyErrorCode =
  /** A decimal the currency cannot represent — `1500000.50` in a currency with no sen. */
  | 'sub_unit_precision'
  /** Not a decimal number at all. */
  | 'malformed_amount'
  /** A currency code the registry does not declare. */
  | 'unknown_currency'
  /** Beyond the range a `number` holds exactly. Refused rather than silently rounded by the float. */
  | 'amount_out_of_range'
  /** A cross-currency operation with no exchange rate. **Never falls back to 1:1** (invariant 04). */
  | 'exchange_rate_missing'
  /** A rate that is zero or negative. Same refusal, different cause, so it reads differently in a log. */
  | 'exchange_rate_not_positive'

export class MoneyError extends Error {
  readonly code: MoneyErrorCode
  readonly detail: Record<string, unknown>

  constructor(code: MoneyErrorCode, message: string, detail: Record<string, unknown> = {}) {
    super(message)
    this.name = 'MoneyError'
    this.code = code
    this.detail = detail
  }
}
